// src/components/SequenceOverview/ContactList/ContactList.jsx
import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Checkbox, Button } from 'antd';
import Loader from '../../../../../components/commonComponents/Loader/Index';

// assets
import Xmark from '../../../../../assets/customSVG/Xmark';

// api
import {
  exportAllContacts,
  getSequenceOverviewContactlists,
} from '../../../api/SquenceOverview';

// hooks
import { useSearch } from '../../../../../hooks/useSearch';
import useDebounce from '../../../../../hooks/useDebounce';

// redux
import { useAspSelector } from '../../../../../test/jest-redux-hooks';

// components
import Filters from '../../../../../components/commonComponents/filters';
import SearchBar from '../../../../../components/commonComponents/searchBar';
import ContactListCard from './ContactListCard';

// Notification 
import NotificationContext from '../../../../../providers/NotificationProvider';
import { showSuccessToast, showErrorToast } from '../../../../../components/commonComponents/gxAntdToast/toastUtils';

import useJsonToExcel from '../../../../../hooks/useJsonToExcel';

// constants
import {
  SORT_FILTERS,
  STATUS_FILTERS,
} from '../../../constants/sequenceOverview';

// helper
import { cn } from '../../../../../helper/cn';

const PAGE_LIMIT = 10;

const ContactList = ({ sequenceDetails }) => {
  const { sequence_id, campaign_id } = useParams();
  const { currentBrand } = useAspSelector(state => state.app);

  // search & debounce
  const { searchKey, setSearchKey, onChange } = useSearch();
  const debouncedSearch = useDebounce(searchKey, 300);

  // filters & sort
  const [statusFilters, setStatusFilters] = useState(STATUS_FILTERS);
  const [sortFilters, setSortFilters] = useState(SORT_FILTERS);

  // data & pagination
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // loading states
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);

  // selection state
  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [excludedIds, setExcludedIds] = useState(new Set());

  const { handleDownloadExcel } = useJsonToExcel({});
  const notification = useContext(NotificationContext);

  const anySelected = selectAll
    ? totalCount - excludedIds.size > 0
    : selectedIds.size > 0;

  // AbortController ref for cancelling page-1 searches
  const abortRef = useRef(null);

  // build filters payload
  const getFiltersPayload = useCallback(() => {
    const status = statusFilters.filter(f => f.isSelected).map(f => f.key);
    const sort = sortFilters.filter(f => f.filter === 'sort' && f.isSelected).map(f => f.key);
    const order = sortFilters.filter(f => f.filter === 'order' && f.isSelected).map(f => f.key);
    const p = {};
    if (status.length) p.status = status;
    if (sort.length) p.sort = sort;
    if (order.length) p.order = order;
    return p;
  }, [statusFilters, sortFilters]);

  // fetch function
  const fetchContacts = async (pageNum = 1, append = false) => {
    // if new search/page-1: cancel previous and set loading
    if (pageNum === 1) {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      setLoading(true);
    } else {
      if (loadingMore) return;
      setLoadingMore(true);
    }

    try {
      const params = {
        campaignId: campaign_id,
        brandId: currentBrand.brand_id,
        limit: PAGE_LIMIT,
        page: pageNum,
        ...getFiltersPayload(),
      };
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await getSequenceOverviewContactlists(
        sequence_id,
        params,
        { signal: abortRef.current?.signal }
      );

      if (res.status === 200) {
        const items = res.data.data;
        const count = res.data.count;
        setTotalCount(count);
        setContacts(prev => (append ? [...prev, ...items] : items));
        setHasMore(pageNum * PAGE_LIMIT < count);
      } else {
        if (!append) setContacts([]);
        setHasMore(false);
      }
    } catch (err) {
      // swallow only aborts
      if (err.name !== 'AbortError') console.warn(err);
    } finally {
      if (pageNum === 1) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  // unified effect: run on mount + search/filters/page changes
  useEffect(() => {
    setPage(1);
    fetchContacts(1, false);
  }, [
    sequenceDetails,
    debouncedSearch,
    JSON.stringify(statusFilters),
    JSON.stringify(sortFilters),
    sequence_id,
    campaign_id,
  ]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchContacts(next, true);
  };

  // reset trigger for manual clears
  const [resetTrigger, setResetTrigger] = useState(false);
  useEffect(() => {
    if (resetTrigger) {
      setPage(1);
      setContacts([]);
      fetchContacts(1, false);
      setResetTrigger(false);
    }
  }, [statusFilters, sortFilters, resetTrigger]);

  const clearAll = (reset = false) => {
    setSearchKey('');
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setSelectAll(false);

    if (reset) {
      setStatusFilters(STATUS_FILTERS);
      setSortFilters(SORT_FILTERS);
    } else {
      setResetTrigger(prev => !prev);
    }
  };

  // selection toggles
  const onToggleAll = checked => {
    setSelectAll(checked);
    setSelectedIds(new Set());
    setExcludedIds(new Set());
  };

  const onToggleRow = id => {
    if (selectAll) {
      setExcludedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    }
  };

  // export handler
  const handleExport = async () => {
    setExporting(true);
    try {
      const base = {
        campaign_id: campaign_id,
        brand_id: currentBrand.brand_id,
        ...getFiltersPayload(),
      };
  
      const payload = selectAll
        ? {
            select_all: true,
            ...base,
            ...(debouncedSearch && { search: debouncedSearch }),
            excluded_ids: Array.from(excludedIds),
          }
        : {
            select_all: false,
            ...base,
            selected_ids: Array.from(selectedIds),
          };
  
      const res = await exportAllContacts(sequence_id, payload);
      if (res.status === 200) {
        const list = res.data?.data || [];
  
        // Format the data for Excel export
        const formattedData = list.map(item => ({
          profile_name: item.profile_name || '',
          phone_country_code: item.phone_country_code || '',
          phone_number: item.phone_number || '',
          status: item.status || '',
          created_at: item.created_at ? new Date(item.created_at).toLocaleString() : '',
        }));
  
        if (formattedData?.length > 0) {
          handleDownloadExcel({
            fileName: (sequenceDetails?.name || 'Contacts').replaceAll(' ', '_'),
            data: formattedData,
            // No need for keys since we're already formatting the data
            keys: [
              { from: 'profile_name', to: 'Profile Name' },
              { from: 'phone_country_code', to: 'Country Code' },
              { from: 'phone_number', to: 'Phone Number' },
              { from: 'status', to: 'Status' },
              { from: 'created_at', to: 'Created At' },
            ],
          });
        }
      } else {
        showErrorToast(notification, 'Export failed; please try again.');
      }
    } catch (error) {
      console.error('Export error:', error);
      showErrorToast(notification, error?.response?.data?.error || error?.message || 'An error occurred during export.');
    } finally {
      setExporting(false);
    }
  };  

  const trStyle = 'text-sm font-semibold';

  return (
    <div className="w-full p-6 bg-white rounded-2xl">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-4 sticky top-0 bg-white z-10">
        {anySelected ? (
          <div className="flex items-center gap-4 w-full justify-between">
            <div className="font-bold">
              {selectAll
                ? `All ${totalCount - excludedIds.size} of ${totalCount} contacts selected`
                : `${selectedIds.size} contact${selectedIds.size !== 1 ? 's' : ''} selected`}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="link"
                icon={<Xmark width={12} color="rgb(105 64 242)" height={12} strokeWidth={2} />}
                onClick={() => clearAll(true)}
                style={{ color: 'rgb(105 64 242)' }}
              >
                Clear
              </Button>
              <Button
                type="primary"
                disabled={!anySelected || exporting}
                loading={exporting}
                onClick={handleExport}
                style={{ backgroundColor: '#05805C' }}
              >
                Export
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-4 w-full justify-between">
            <SearchBar
              searchKey={searchKey}
              setSearchKey={setSearchKey}
              onChange={onChange}
              expandable={false}
              className="flex-1 max-w-[30%]"
              onClear={clearAll}
            />
            <div className="flex items-center gap-2">
              <Filters
                type="multiple"
                label="Status"
                lists={statusFilters}
                setLists={setStatusFilters}
                reset={clearAll}
              />
              <Filters
                type="single"
                label="Sort"
                lists={sortFilters}
                setLists={setSortFilters}
              />
              <Button
                type="primary"
                disabled={(!selectAll && selectedIds.size === 0) || exporting}
                loading={exporting}
                onClick={handleExport}
              >
                Export
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Table Header */}
      <div className="flex items-center gap-2.5 py-3 px-2.5 border-b">
        <div className={cn('w-[4%]', trStyle)}>
          <Checkbox
            checked={selectAll}
            // indeterminate={!selectAll && anySelected}
            onChange={e => onToggleAll(e.target.checked)}
            className={!selectAll && anySelected ? 'custom-indeterminate-checkbox' : ''}
          />
          <style jsx global>{`
            .custom-indeterminate-checkbox .ant-checkbox-inner::after {
              background-color: #22c55e !important;
            }
            .custom-indeterminate-checkbox.ant-checkbox-checked .ant-checkbox-inner {
              background-color: #22c55e !important;
              border-color: #22c55e !important;
            }
          `}</style>
        </div>
        <div className={cn('w-[30%]', trStyle)}>Name</div>
        <div className={cn('w-[25%]', trStyle)}>Mobile Number</div>
        <div className={cn('w-[25%]', trStyle)}>Start Time</div>
        <div className={cn('w-[20%]', trStyle)}>Status</div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader Width="24px" Height="24px" />
        </div>
      ) : contacts.length ? (
        <div id="scrollableDiv" className="h-64 overflow-auto">
          <InfiniteScroll
            dataLength={contacts.length}
            next={loadMore}
            hasMore={hasMore}
            scrollableTarget="scrollableDiv"
            loader={
              loadingMore && (
                <div className="flex justify-center py-2">
                  <Loader Width="24px" Height="24px" />
                </div>
              )
            }
          >
            {contacts.map(contact => (
              <div
                key={contact.id}
                className="flex items-center h-14 px-2 my-1 odd:bg-[#FAFAFA] rounded-lg"
              >
                <ContactListCard
                  data={contact}
                  isSelected={
                    selectAll
                      ? !excludedIds.has(contact.id)
                      : selectedIds.has(contact.id)
                  }
                  onToggle={() => onToggleRow(contact.id)}
                  trStyle={trStyle}
                />
              </div>
            ))}
          </InfiniteScroll>
        </div>
      ) : (
        <div className="flex items-center justify-center h-44">No data</div>
      )}
    </div>
  );
};

export default ContactList;
